<script lang="ts" setup>
import VueSelect from 'vue-select'

type SelectValue = string | number | undefined
type SelectOption = { value?: SelectValue; label: string }
type VueSlotAttributes = Record<string, unknown>
type VueSlotEvents = Record<string, (...args: unknown[]) => void>

// Define the type for the context prop
interface SelectContext {
  id?: string
  value?: SelectValue
  attrs: {
    'select-options': SelectOption[]
    'handle-focus-fn': (focused: boolean) => void
    required?: boolean
  }
  node: {
    input: (value: SelectValue | string) => void
  }
}

const props = defineProps<{
  context: SelectContext
}>()

const initialValue = props.context.attrs['select-options'].find((option) => option.value === props.context.value)

const selected = ref(initialValue)

const resolveSelected = () => {
  const value = props.context.value
  if (!value) return
  const match = (props.context.attrs['select-options'] ?? []).find((option) => option.value === value)
  if (match !== selected.value) {
    selected.value = match
  }
}

watch(
  () => props.context.value,
  (value) => {
    if (!value) return
    if (value === selected.value?.value) return
    selected.value = props.context.attrs['select-options'].find((option) => option.value === value)
  },
)

// Re-resolve the displayed object when the available options change (e.g.
// asynchronous arrival or a new locale's labels). Only the local display
// object is updated; the saved value and the FormKit node are never written.
watch(() => props.context.attrs['select-options'], resolveSelected)
</script>

<template>
  <VueSelect
    v-model="selected"
    :input-id="context?.id"
    :uid="context?.id"
    :options="context?.attrs['select-options'] ?? []"
    :clearable="false"
    @option:selected="(option) => context?.node.input(option.value ?? option.label)"
    @open="context?.attrs['handle-focus-fn'](true)"
    @close="() => context?.attrs['handle-focus-fn'](false)"
  >
    <template v-if="context?.attrs.required" #search="{ attributes, events }">
      <input
        class="vs__search"
        aria-required="true"
        v-bind="attributes as VueSlotAttributes"
        v-on="events as VueSlotEvents"
      />
    </template>
    <template #open-indicator="{ toggleAttributes }">
      <span v-bind="toggleAttributes as {}"><Icon name="dropdown" /></span>
    </template>
  </VueSelect>
</template>

<style lang="scss" scoped></style>
